import { useEffect, useState } from "react";
import { Patient } from "../../../utils/api-objects/Patient";
import { usePatients } from "../../../utils/context/PatientsContext";

// Function to aggregate patients by user_id (same logic as in usePatientFilters)
const aggregatePatientsByUserId = (patients: Patient[]): Patient[] => {
  const patientMap = new Map<string, Patient>();

  patients.forEach((patient) => {
    const existingPatient = patientMap.get(patient.user_id);
    
    if (!existingPatient) {
      // First occurrence of this user_id
      patientMap.set(patient.user_id, patient);
    } else {
      // Merge with existing patient record
      const mergedPatient: Patient = {
        ...existingPatient,
        // Combine products arrays
        products: Array.from(new Set([...existingPatient.products, ...patient.products])),
        // Combine bloodcodes arrays
        bloodcodes: Array.from(new Set([...existingPatient.bloodcodes, ...patient.bloodcodes])),
        // Use the most recent created_at
        created_at: new Date(patient.created_at) > new Date(existingPatient.created_at) 
          ? patient.created_at 
          : existingPatient.created_at,
        // Use the most recent status (prioritize active over canceled)
        status: patient.status === 'active' || existingPatient.status === 'active' 
          ? 'active' 
          : patient.status,
        // Use the most recent sub_type (prioritize non-empty)
        sub_type: patient.sub_type || existingPatient.sub_type,
        // Use the most recent swabcode (prioritize non-null)
        swabcode: patient.swabcode || existingPatient.swabcode,
      };
      
      patientMap.set(patient.user_id, mergedPatient);
    }
  });

  return Array.from(patientMap.values());
};

export const useGetPatientById = (patientId: string | null) => {
    const { patientsState } = usePatients()
    const patients = patientsState.patients

    const [patient, setPatient] = useState<Patient | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!patientId || !patients) {
            setIsLoading(false);
            return;
        }

        // First aggregate patients to handle duplicates, then find the specific patient
        const aggregatedPatients = aggregatePatientsByUserId(patients);
        const found = aggregatedPatients.find(p => p.user_id === patientId);
        setPatient(found || null);
        setIsLoading(false);
    }, [patientId, patients])

    return { patient, isLoading };
}